import React, { useMemo } from 'react';
import { NavigateFunction } from 'react-router-dom';
import styled, { css } from 'styled-components';

import type { CheckDepositBatch } from '@column/api';
import { Chip, Icon, Tooltip, formatNumber } from '@column/column-ui-kit';
import { CurrencyFlag } from '@column/ui';

import { ProgressBar } from '~/app/pages/Transfers/_components/ProgressBar';
import { ROUTE } from '~/app/routes';
import { CopyRow, CustomFieldWrapper } from '~/components/CopyRow';
import { QuickLookImage } from '~/components/QuickLookImage';
import { UnderlineLink } from '~/elements';
import { Map } from '~/elements/Map';
import { FeatureFlag, useFeatureFlag } from '~/lib/flags';
import { useNavigate } from '~/lib/navigation';
import { checkReturnCodesMap, returnCodesTooltipMap } from '~/repositories';
import { useSessionStore } from '~/stores/Session';
import { Card, Flex, Inner, Text } from '~/styles';
import { ACHTransferBatch, TransferReview } from '~/typings/API';
import { TransferReviewDecision } from '~/typings/enum';
import { formatDate, formatString, formatTime } from '~/util';

import { AchBatchWrapper } from './AchBatchWrapper';
import { Avatar, AvatarVariant } from './Avatar';
import { BatchWrapper } from './BatchWrapper';
import { TimelineEventDetails } from './TimelineEventDetails';
import {
  TimelinePartyType,
  TimelineStatus,
  TransferAdminParty,
  TransferAmount,
  TransferBankAccount,
  TransferCheckPayee,
  TransferCompany,
  TransferCounterparty,
  TransferInternationalWireReturn,
  TransferParty,
  TransferReturn,
} from './types';

interface TimelineEventProps {
  amount?: TransferAmount;
  batch?: CheckDepositBatch;
  achBatch?: ACHTransferBatch;
  date?: Date | string;
  link?: { label: string; path: string };
  label: string;
  status?: TimelineStatus;
  partyData?: TransferParty;
  returnData?: TransferReturn;
  internationalWireReturnData?: TransferInternationalWireReturn;
  reviewData?: TransferReview[];
  showDate?: boolean;
}

const Event = styled.div<{ $isLast?: boolean }>`
  display: grid;
  align-items: start;
  grid-column-gap: 24px;
  grid-template-rows: auto;
  grid-row-gap: 8px;
  position: relative;

  > *:nth-child(3) {
    grid-column: 1 / -1;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: minmax(180px, 25%) 1fr;
    grid-template-rows: auto auto;

    > *:nth-child(3) {
      grid-column: 1 / -1;
    }
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.wide}) {
    grid-template-columns: minmax(180px, 25%) auto 25%;
    grid-template-rows: auto;

    > *:nth-child(3) {
      grid-column: auto;
    }
  }
`;

const TimeLocal = styled.span`
  transition: opacity 0.2s ease-in-out;
  opacity: 1;
  position: absolute;
`;

const TimeUTC = styled(TimeLocal)`
  opacity: 0;
  position: relative;
`;

const TimeDisplay = styled.span`
  display: flex;
  gap: 8px;
`;

const Status = styled.div<{ status?: TimelineStatus }>`
  min-width: 248px;
  height: 100%;
  padding: 14px 0 0;
  box-sizing: border-box;

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    padding: 14px 0;
  }

  &:after {
    content: '';
    width: 2px;
    border-radius: 1px;
    background: ${({ theme }) => theme.secondary.blendToBackground(200)};
    position: absolute;
    top: 42px;
    bottom: -9px;
    left: 9px;
  }

  ${Event}:last-child &:after {
    display: none;
  }

  h4 {
    display: flex;
    gap: 8px;
    align-items: center;
    color: ${({ theme, status }) => theme.secondary.blendToBackground(status === TimelineStatus.Pending ? 600 : 1000)};
  }

  p {
    font-size: 12px;
    line-height: 18px;
    color: ${({ theme, status }) => theme.secondary.blendToBackground(status === TimelineStatus.Pending ? 400 : 800)};
    margin: 0 0 16px 28px;

    &:hover {
      ${TimeLocal} {
        opacity: 0;
      }

      ${TimeUTC} {
        opacity: 1;
      }
    }

    a {
      cursor: pointer;
      display: flex;
      align-items: center;

      svg {
        --icon-color: var(--color-primary);
        opacity: 0;
        transform: translateX(-4px);
        transition:
          color 0.2s,
          transform 0.2s,
          opacity 0.2s;
      }

      &:hover {
        color: ${({ theme }) => theme.secondary.background};

        svg {
          opacity: 1;
          transform: translateX(0);
        }
      }
    }
  }

  svg {
    color: ${({ theme, status }) =>
      status === TimelineStatus.Pending || status === TimelineStatus.InProgress
        ? theme.secondary.blendToBackground(status === TimelineStatus.Pending ? 600 : 1000)
        : 'var(--color-muted-foreground)'};
    --icon-color: currentColor;

    ${({ status }) =>
      status === TimelineStatus.Success &&
      css`
        color: var(--color-seafoam-600);
      `}

    ${({ status }) =>
      (status === TimelineStatus.Danger || status === TimelineStatus.Returned) &&
      css`
        color: var(--color-red-500);
      `}
  }
`;

const CurrencyAmount = styled(Flex)`
  svg {
    --icon-size: 16px;
  }
`;

const StyledBatchWrapper = styled(BatchWrapper)`
  margin-left: 0;

  > div > div:nth-child(2) {
    margin-left: 0;
  }
`;

// Matches the 16px bottom spacing of TimelineEventDetails' StyledCard so return cards
// stack with the same rhythm as the other cards in the center column.
const ReturnCard = styled(Card)`
  margin-bottom: 16px;
`;

const AddressContainer = styled.div`
  padding: 16px;

  @container copy-row (min-width: ${({ theme }) => theme.containerBreakpoints.tablet}) {
    padding: 16px 16px 16px 34px;
  }
`;

const TrackingLinkWrapper = styled(CustomFieldWrapper)`
  align-self: stretch;
  display: flex;
  align-items: center;
  font-size: 14px;
  white-space: nowrap;
`;

const ContentWrapper = ({
  batch,
  achBatch,
  children,
}: {
  children: React.ReactNode;
  batch?: CheckDepositBatch;
  achBatch?: ACHTransferBatch;
}) => {
  if (achBatch) return <AchBatchWrapper batch={achBatch}>{children}</AchBatchWrapper>;
  if (batch) return <StyledBatchWrapper batch={batch}>{children}</StyledBatchWrapper>;
  return children;
};

const getReviewDecisionIconStyle = (decision: string) =>
  ({
    '--icon-color': decision === TransferReviewDecision.Approved ? 'var(--color-seafoam-600)' : 'var(--color-red-500)',
    color: decision === TransferReviewDecision.Approved ? 'var(--color-seafoam-600)' : 'var(--color-red-500)',
  }) as React.CSSProperties;

export const TimelineEvent: React.FC<TimelineEventProps> = ({
  amount,
  batch,
  achBatch,
  date,
  label,
  link,
  status,
  partyData,
  returnData,
  internationalWireReturnData,
  reviewData,
  showDate,
}) => {
  const { isSandbox } = useSessionStore();
  const navigate = useNavigate();
  const showCheckDeliveryMethod = useFeatureFlag(FeatureFlag.EnableExpeditedCheckDelivery);

  const eventDetails = useMemo(() => {
    if (!partyData) return;
    return buildTransferEventDetailsFromParty(
      partyData.data,
      !!isSandbox,
      navigate,
      partyData.metadata,
      partyData.partyType,
      showCheckDeliveryMethod
    );
  }, [isSandbox, navigate, partyData, showCheckDeliveryMethod]);

  const icon =
    status === TimelineStatus.Pending ? (
      <Icon.CircleBlank />
    ) : status === TimelineStatus.Danger ? (
      <Icon.CircleCross />
    ) : status === TimelineStatus.Warning ? (
      <Icon.CircleCheck />
    ) : status === TimelineStatus.Returned ? (
      <Icon.CircleReturn />
    ) : status === TimelineStatus.InProgress ? (
      <Icon.CirclePendingApproval />
    ) : (
      <Icon.CircleCheck />
    );

  const dateContent = date ? (
    typeof date === 'string' ? (
      date
    ) : (
      <>
        <TimeDisplay>
          <TimeLocal>
            {formatDate(date, { timeZoneFormat: 'local' })}
            <br />
            {formatTime(date, { timeZoneFormat: 'local' })}
          </TimeLocal>
          <TimeUTC>
            {formatDate(date, { timeZoneFormat: 'utc' })}
            <br />
            {formatTime(date, { timeZoneFormat: 'utc' })}
          </TimeUTC>
        </TimeDisplay>
      </>
    )
  ) : null;

  const tooltipDateContent = date ? (
    typeof date === 'string' ? (
      date
    ) : (
      <>
        <Text size={12} color="inherit">
          {formatDate(date, { timeZoneFormat: 'local' })}
          <br />
          {formatTime(date, { timeZoneFormat: 'local' })}
        </Text>
        <Text size={12} color="lighter">
          {formatDate(date, { timeZoneFormat: 'utc' })}
          <br />
          {formatTime(date, { timeZoneFormat: 'utc' })}
        </Text>
      </>
    )
  ) : null;

  const StatusContent = (
    <Status status={status || TimelineStatus.Success}>
      {date && !showDate ? (
        <Text as="h4" size={14} weight="medium" lineHeight="24px">
          {icon}
          <Tooltip content={tooltipDateContent} placement="bottom-start" offsetArrowX={-52} offsetX={-12}>
            {link ? (
              <a onClick={() => navigate(link.path)} style={{ cursor: 'pointer' }}>
                {label}
              </a>
            ) : (
              label
            )}
          </Tooltip>
        </Text>
      ) : (
        <Text as="h4" size={14} weight="medium" lineHeight="24px">
          {icon}
          {link ? (
            <a onClick={() => navigate(link.path)} style={{ cursor: 'pointer' }}>
              {label}
            </a>
          ) : (
            label
          )}
        </Text>
      )}

      {date && showDate && (
        <Text as="p" size={12} color="lighter">
          {dateContent}
        </Text>
      )}

      {link && (
        <Text as="p" size={12} color="lighter">
          <a onClick={() => navigate(link.path)}>
            {link.label}
            <Icon.ChevronRight />
          </a>
        </Text>
      )}
    </Status>
  );

  return (
    <Event>
      {StatusContent}

      <ContentWrapper batch={batch} achBatch={achBatch}>
        {returnData && !internationalWireReturnData && (
          <Flex flexDirection="column">
            {eventDetails && (
              <TimelineEventDetails
                details={eventDetails.details}
                icon={eventDetails.icon}
                onClick={eventDetails.onClick}
                title={eventDetails.name}
                metadata={eventDetails.metadata}
                amount={
                  amount
                    ? {
                        ...amount,
                        value: amount.value || 0,
                        partyType: partyData?.partyType === TimelinePartyType.Receiver ? 'receiver' : 'sender',
                      }
                    : undefined
                }
              />
            )}

            <ReturnCard variant="secondary">
              {returnData.code && (
                <CopyRow
                  label="Return Code"
                  value={
                    <CustomFieldWrapper>
                      <Flex flexDirection="column" gap={8}>
                        <Chip size="large" type="danger">
                          {returnData.code}
                        </Chip>
                        <Text size={12}>{returnCodesTooltipMap.get(returnData.code)}</Text>
                      </Flex>
                    </CustomFieldWrapper>
                  }
                />
              )}

              {returnData.reason && (
                <CopyRow
                  label="Return Reason"
                  value={
                    <CustomFieldWrapper>
                      <Flex flexDirection="column" gap={8}>
                        <Chip size="large" type="danger">
                          {returnData.reason}
                        </Chip>
                        <Text size={12}>{checkReturnCodesMap.get(returnData.reason)}</Text>
                      </Flex>
                    </CustomFieldWrapper>
                  }
                />
              )}

              {returnData.description && <CopyRow label="Return Description" value={returnData.description || ''} />}

              {returnData.addenda && <CopyRow label="Return Addenda" value={returnData.addenda || ''} />}

              {returnData.amount && (
                <CopyRow
                  label="Return Amount"
                  value={
                    <CustomFieldWrapper>
                      <Flex flexDirection="column" gap={8}>
                        <Chip size="large" type="danger">
                          {formatNumber(returnData.amount)} {returnData.currencyCode}
                        </Chip>
                      </Flex>
                    </CustomFieldWrapper>
                  }
                />
              )}
            </ReturnCard>
          </Flex>
        )}
        {internationalWireReturnData && (
          <ReturnCard variant="secondary">
            <CopyRow label="Return Reason" value={internationalWireReturnData.reason} />
            <CopyRow
              label="Return Amount"
              value={
                <CustomFieldWrapper>
                  <CurrencyAmount gap={8}>
                    <CurrencyFlag currency={internationalWireReturnData.currencyCode.slice(-3)} />
                    <Flex alignItems="baseline" gap={4}>
                      <Text size={14} weight="medium">
                        {formatNumber(internationalWireReturnData.amount)}
                      </Text>
                      <Text size={14} color="lighter">
                        {internationalWireReturnData.currencyCode}
                      </Text>
                    </Flex>
                  </CurrencyAmount>
                </CustomFieldWrapper>
              }
            />
          </ReturnCard>
        )}

        {partyData && (!returnData || internationalWireReturnData) && (
          <TimelineEventDetails
            details={eventDetails?.details || []}
            icon={eventDetails?.icon}
            status={status}
            onClick={eventDetails?.onClick || undefined}
            subtitle={
              amount
                ? `${amount.isAdding === false ? '-' : ''}${formatNumber(
                    partyData.partyType === TimelinePartyType.Receiver
                      ? amount.settledAmount || amount.value
                      : amount.value,
                    partyData.partyType === TimelinePartyType.Receiver
                      ? amount.settledCurrencyCode
                      : amount.currencyCode
                  )}`
                : undefined
            }
            amount={
              amount
                ? {
                    ...amount,
                    value: amount.value || 0,
                    partyType: partyData?.partyType === TimelinePartyType.Receiver ? 'receiver' : 'sender',
                  }
                : undefined
            }
            title={eventDetails?.name || 'Unnamed'}
            metadata={eventDetails?.metadata}
          />
        )}

        {reviewData &&
          reviewData.map((transferReview, idx) => (
            <TimelineEventDetails
              key={`transfer-review-${idx}`}
              details={transferReview.comment ? [{ label: 'Comment', value: transferReview.comment }] : undefined}
              icon={
                <Avatar
                  size="small"
                  variant={
                    transferReview.decision === TransferReviewDecision.Approved
                      ? AvatarVariant.Success
                      : AvatarVariant.Danger
                  }
                >
                  <span style={getReviewDecisionIconStyle(transferReview.decision)}>
                    {transferReview.decision === TransferReviewDecision.Approved ? <Icon.Checkmark /> : <Icon.Cross />}
                  </span>
                </Avatar>
              }
              title={formatString(transferReview.decision)}
              secondaryTitle={`by ${transferReview.reviewedBy.name}`}
              variant="secondary"
            />
          ))}
      </ContentWrapper>
    </Event>
  );
};

const buildTransferEventDetailsFromParty = (
  party: TransferBankAccount | TransferCheckPayee | TransferCounterparty | TransferCompany | TransferAdminParty,
  isSandbox: boolean,
  navigate: NavigateFunction,
  metadata?: string,
  partyType?: TimelinePartyType,
  showCheckDeliveryMethod?: boolean
):
  | {
      name: string;
      icon: React.ReactNode;
      details?: { label: string; value: string | React.ReactNode; warning?: string }[];
      onClick?: () => void;
      metadata?: string;
    }
  | undefined => {
  if ((party as TransferBankAccount).bankAccount) {
    return buildTransferEventDetailsFromBankAccount(
      party as TransferBankAccount,
      isSandbox,
      navigate,
      metadata,
      partyType
    );
  }
  if ((party as TransferCounterparty).counterparty) {
    return buildTransferEventDetailsFromCounterparty(party as TransferCounterparty, navigate);
  }
  if ((party as TransferCompany).companyName) {
    return buildTransferEventDetailsFromCompany(party as TransferCompany);
  }
  if ((party as TransferAdminParty).adminName) {
    return buildTransferEventDetailsFromAdminParty(party as TransferAdminParty);
  }
  if (
    (party as TransferCheckPayee).payeeName ||
    (party as TransferCheckPayee).frontImage ||
    (party as TransferCheckPayee).backImage ||
    (party as TransferCheckPayee).deliveredByColumn ||
    (party as TransferCheckPayee).payeeAddress
  ) {
    return buildTransferEventDetailsFromCheckPayee(party as TransferCheckPayee, showCheckDeliveryMethod);
  }
  return undefined;
};

const buildTransferEventDetailsFromBankAccount = (
  { accountNumber, bankAccount, entityType, isBookTransfer, frontImage, backImage }: TransferBankAccount,
  isSandbox: boolean,
  navigate: NavigateFunction,
  metadata?: string,
  partyType?: TimelinePartyType
) => {
  let icon: React.ReactNode;
  if (entityType) {
    icon = entityType === 'person' ? <Icon.User /> : <Icon.Business />;
  } else {
    icon = <Icon.Bank />;
  }

  // Only apply the sender/receiver prefixes for book transfers
  const prefix =
    isBookTransfer && partyType
      ? partyType === TimelinePartyType.Sender
        ? 'Sender '
        : partyType === TimelinePartyType.Receiver
          ? 'Receiver '
          : ''
      : '';

  const details: { label: string; value: string | React.ReactNode; warning?: string }[] = [
    { label: `${prefix}Bank Account ID`, value: bankAccount.id },
  ];
  // For book transfers, don't show routing number for receiver as it will be the same as sender
  const showRoutingNumber = !(isBookTransfer && partyType === TimelinePartyType.Receiver);

  if (showRoutingNumber) {
    details.push({ label: 'Routing Number', value: accountNumber.routingNumber || '' });
  }

  details.push({
    label: `${prefix}Account Number`,
    value: accountNumber.accountNumber || '',
    warning: isSandbox ? 'Account numbers created in sandbox mode cannot be used to receive funds.' : undefined,
  });

  const detailsWithImages = [...details];

  if (frontImage) {
    detailsWithImages.push({
      label: 'Check Front',
      value: (
        <Inner pr={16} pl={8} py={16}>
          <QuickLookImage src={`data:image/tiff;base64,${frontImage}`} errorMessage="Front image could not be loaded" />
        </Inner>
      ),
    });
  }

  if (backImage) {
    detailsWithImages.push({
      label: 'Check Back',
      value: (
        <Inner pr={16} pl={8} py={16}>
          <QuickLookImage src={`data:image/tiff;base64,${backImage}`} errorMessage="Back image could not be loaded" />
        </Inner>
      ),
    });
  }

  return {
    name: bankAccount.displayName || bankAccount.description || 'Unnamed Bank Account',
    onClick: () => navigate(`${ROUTE.BANK_ACCOUNTS}/edit/${bankAccount?.id}`),
    icon: <Avatar size="large">{icon}</Avatar>,
    details: detailsWithImages,
    metadata,
  };
};

const StatusContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;

  @container copy-row (min-width: ${({ theme }) => theme.containerBreakpoints.tablet}) {
    padding: 16px 16px 16px 34px;
  }
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StatusIcon = styled.div<{ $variant?: 'pending' | 'success' | 'danger' }>`
  flex-shrink: 0;
  svg {
    --icon-size: 16px;
    --icon-color: ${({ $variant }) => {
      if ($variant === 'success') {
        return 'var(--color-seafoam-600)';
      }
      if ($variant === 'danger') {
        return 'var(--color-red-500)';
      }
      return 'var(--color-muted-foreground)';
    }};
  }
`;

const FAILED_STATUSES = new Set([
  'failed',
  'rejected',
  'rerouted',
  'returned_to_sender',
  'returnedToSender',
  'international_exit',
  'internationalExit',
]);

const CHECK_DELIVERY_PROGRESS_STEPS = [
  { status: 'created', percent: 0 },
  { status: 'rendered_pdf', percent: 14 },
  { status: 'renderedPdf', percent: 14 },
  { status: 'mailed', percent: 28 },
  { status: 'in_transit', percent: 42 },
  { status: 'inTransit', percent: 42 },
  { status: 'in_local_area', percent: 57 },
  { status: 'inLocalArea', percent: 57 },
  { status: 'processed_for_delivery', percent: 71 },
  { status: 'processedForDelivery', percent: 71 },
  { status: 'delivered', percent: 100 },
];

const CHECK_DELIVERY_METHOD_LABELS: Record<string, string> = {
  usps_first_class: 'USPS First Class',
  usps_priority: 'USPS Priority',
  fedex_two_day: 'FedEx Two Day',
  fedex_overnight: 'FedEx Overnight',
};

const SAFE_DELIVERY_TRACKING_HOSTNAMES = new Set(['tools.usps.com', 'www.fedex.com', 'www.ups.com']);

const getSafeDeliveryTrackingUrl = (trackingUrl?: string | null) => {
  if (!trackingUrl) return undefined;

  try {
    const url = new URL(trackingUrl);
    if (url.protocol === 'https:' && SAFE_DELIVERY_TRACKING_HOSTNAMES.has(url.hostname)) {
      return trackingUrl;
    }
  } catch {
    return undefined;
  }
};

const formatCheckDeliveryStatus = (status?: string): string => {
  if (!status) return '';

  const statusMap: Record<string, string> = {
    created: 'Created',
    rendered_pdf: 'Rendered PDF',
    renderedPdf: 'Rendered PDF',
    mailed: 'Mailed',
    in_transit: 'In Transit',
    inTransit: 'In Transit',
    in_local_area: 'In Local Area',
    inLocalArea: 'In Local Area',
    processed_for_delivery: 'Processed For Delivery',
    processedForDelivery: 'Processed For Delivery',
    delivered: 'Delivered',
    failed: 'Failed',
    rerouted: 'Rerouted',
    returned_to_sender: 'Returned To Sender',
    returnedToSender: 'Returned To Sender',
    rejected: 'Rejected',
    international_exit: 'International Exit',
    internationalExit: 'International Exit',
  };

  if (statusMap[status]) {
    return statusMap[status];
  }

  if (status.includes('_')) {
    return status
      .split('_')
      .map((word) => {
        if (word.toLowerCase() === 'pdf') {
          return 'PDF';
        }
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');
  }

  return status
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const getCheckDeliveryProgress = (status?: string): { percent: number; isFailed: boolean; isCompleted: boolean } => {
  if (!status) {
    return { percent: 0, isFailed: false, isCompleted: false };
  }

  const normalizedStatus = status.toLowerCase();

  if (FAILED_STATUSES.has(normalizedStatus) || FAILED_STATUSES.has(status)) {
    return { percent: 0, isFailed: true, isCompleted: false };
  }

  const progressStep = CHECK_DELIVERY_PROGRESS_STEPS.find(
    (s) => s.status === status || s.status.toLowerCase() === normalizedStatus
  );
  const isCompleted = normalizedStatus === 'delivered';
  return { percent: progressStep?.percent || 0, isFailed: false, isCompleted };
};

const getCheckDeliveryStatusIcon = (
  status?: string
): { icon: React.ReactNode; variant: 'pending' | 'success' | 'danger' } => {
  if (!status) {
    return { icon: <Icon.CirclePendingApproval />, variant: 'pending' };
  }

  const normalizedStatus = status.toLowerCase();

  if (FAILED_STATUSES.has(normalizedStatus) || FAILED_STATUSES.has(status)) {
    return { icon: <Icon.CircleCross />, variant: 'danger' };
  }

  if (normalizedStatus === 'delivered') {
    return { icon: <Icon.CircleCheck />, variant: 'success' };
  }

  return { icon: <Icon.CirclePendingApproval />, variant: 'pending' };
};

export const buildTransferEventDetailsFromCheckPayee = (
  {
    payeeName,
    deliveredByColumn,
    payeeAddress,
    deliveryMethod,
    deliveryTrackingNumber,
    deliveryTrackingUrl,
    deliveryStatus,
    frontImage,
    backImage,
    previewPDFAction,
  }: TransferCheckPayee,
  showCheckDeliveryMethod?: boolean
) => {
  const details: { label: string; value: string | React.ReactNode; warning?: string }[] = [];
  const safeDeliveryTrackingUrl = getSafeDeliveryTrackingUrl(deliveryTrackingUrl);

  if (deliveredByColumn && payeeAddress) {
    if (deliveryStatus) {
      const { percent, isFailed } = getCheckDeliveryProgress(deliveryStatus);
      const { icon, variant } = getCheckDeliveryStatusIcon(deliveryStatus);
      details.push({
        label: 'Delivery Status',
        value: (
          <StatusContainer>
            <StatusRow>
              <StatusIcon $variant={variant}>{icon}</StatusIcon>
              <Text size={14} weight="medium">
                {formatCheckDeliveryStatus(deliveryStatus)}
              </Text>
            </StatusRow>
            <ProgressBar percent={percent} isFailed={isFailed} />
          </StatusContainer>
        ),
      });
    }

    if (showCheckDeliveryMethod && deliveryMethod) {
      details.push({
        label: 'Delivery Method',
        value: CHECK_DELIVERY_METHOD_LABELS[deliveryMethod] ?? formatString(deliveryMethod),
      });

      if (deliveryTrackingNumber || safeDeliveryTrackingUrl) {
        const trackingLinkText = deliveryTrackingNumber ?? 'View tracking';
        const trackingLinkAriaLabel = deliveryTrackingNumber
          ? `Track package ${deliveryTrackingNumber} (opens in a new tab)`
          : 'Track package (opens in a new tab)';

        details.push({
          label: 'Tracking Number',
          value: safeDeliveryTrackingUrl ? (
            <TrackingLinkWrapper>
              <UnderlineLink
                href={safeDeliveryTrackingUrl}
                newTab
                iconRight={<Icon.External />}
                ariaLabel={trackingLinkAriaLabel}
              >
                {trackingLinkText}
              </UnderlineLink>
            </TrackingLinkWrapper>
          ) : (
            deliveryTrackingNumber
          ),
        });
      }
    }

    details.push({
      label: 'Mail To Address',
      value: (
        <AddressContainer>
          <Text as="address" size={14} lineHeight="body" color="light">
            {payeeAddress.line1}
            <br />
            {payeeAddress.line2 && (
              <>
                {payeeAddress.line2}
                <br />
              </>
            )}
            {payeeAddress.city}, {payeeAddress.state} {payeeAddress.postalCode}{' '}
            {payeeAddress.countryCode && payeeAddress.countryCode === 'US' && 'United States'}
          </Text>
          <Map address={payeeAddress} width="min(100%, 420px)" margin="16px 0 0" />
        </AddressContainer>
      ),
    });
  }

  if (previewPDFAction) {
    details.push({
      label: 'Check Preview',
      value: (
        <Inner pr={16} pl={8} py={16}>
          {previewPDFAction}
        </Inner>
      ),
    });
  }

  if (frontImage) {
    details.push({
      label: 'Check Front',
      value: (
        <Inner pr={16} pl={8} py={16}>
          <QuickLookImage src={`data:image/tiff;base64,${frontImage}`} errorMessage="Front image could not be loaded" />
        </Inner>
      ),
    });
  }

  if (backImage) {
    details.push({
      label: 'Check Back',
      value: (
        <Inner pr={16} pl={8} py={16}>
          <QuickLookImage src={`data:image/tiff;base64,${backImage}`} errorMessage="Back image could not be loaded" />
        </Inner>
      ),
    });
  }

  return {
    name: payeeName || 'Unnamed Payee',
    icon: (
      <Avatar size="large">
        <Icon.User />
      </Avatar>
    ),
    details,
  };
};

const buildTransferEventDetailsFromAdminParty = ({ adminName }: TransferAdminParty) => {
  return {
    name: adminName,
    icon: (
      <Avatar size="large">
        <Icon.Transfer.Admin />
      </Avatar>
    ),
    details: [],
    metadata: undefined,
  };
};

const buildTransferEventDetailsFromCounterparty = (
  { counterparty }: TransferCounterparty,
  navigate: NavigateFunction
) => {
  return {
    name: counterparty.name || counterparty.description || 'Unnamed Counterparty',
    onClick: () => navigate(`${ROUTE.COUNTERPARTIES}/edit/${counterparty?.id}`),
    icon: <Avatar size="large">{counterparty.legalType === 'individual' ? <Icon.User /> : <Icon.Business />}</Avatar>,
    details: [
      { label: 'Counterparty ID', value: counterparty.id || '' },
      { label: 'Routing Number', value: String(counterparty.routingNumber) },
      {
        label: 'Account Number',
        value: String(counterparty.accountNumber),
      },
    ],
    metadata: undefined,
  };
};

const buildTransferEventDetailsFromCompany = ({ companyId, companyName }: TransferCompany) => {
  return {
    name: companyName || 'Unnamed Company',
    icon: (
      <Avatar size="large">
        <Icon.Business />
      </Avatar>
    ),
    details: [{ label: 'Company ID', value: String(companyId) || '' }],
    metadata: undefined,
  };
};
